/**
 * Admin — Geocoding.
 *
 * Central home for the geocod.io pipeline that resolves contact and account
 * addresses into the vba_geocode table (consumed by the Broker Map and the
 * Groups Maps). Moved here from the Sales admin page 9/04: it is cross-module
 * data on a nightly schedule, not a Sales feature.
 *
 * Three cards: the nightly scheduler's status (the system of record for
 * "is this healthy"), coverage numbers on demand (candidate counts vs stored
 * geocodes), and a manual preview/run for right after a bulk address import.
 * Every run spends paid geocod.io credits; the BFF gates all of it on
 * isAnyModuleAdmin and rate-limits runs.
 */

import { useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle2,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular, EyeRegular, PlayRegular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { bffFetch } from '@shared/api';
import { veracityColors } from '@shared/ui';

// =============================================================================
// API
// =============================================================================

interface NightlyStatus {
  enabled: boolean;
  timeZone: string;
  runHours: number[];
  running: boolean;
  lastRunKey?: string;
  lastRunAt?: string;
  lastSummary?: {
    processed: number;
    geocoded: number;
    skipped: number;
    errors: number;
    capped: boolean;
  };
  lastError?: string;
}

interface GeocodeStatus {
  configured: boolean;
  batchSize: number;
  maxPerRun: number;
  delayBetweenBatches: number;
  hardCap: number;
  nightly: NightlyStatus | null;
}

interface RunResult {
  processed: number;
  geocoded: number;
  skipped: number;
  errors: number;
  capped: boolean;
  details: string[];
}

interface Diagnostics {
  geocodeTable: {
    totalRecords: number;
    byEntityType: Record<string, { total: number; withCoords: number; noCoords: number }>;
  };
  dataverse: {
    contactsWithAddress: number;
    accountsWithAddress: number;
  };
  geocodedContactIds: number;
  geocodedAccountIds: number;
  samples: {
    noCoords: Array<{ id: string; type: string; address?: string }>;
  };
}

async function fetchStatus(): Promise<GeocodeStatus> {
  const res = await bffFetch('/geocode/status');
  if (!res.ok) throw new Error(`Failed to load geocode status (${res.status})`);
  return (await res.json()) as GeocodeStatus;
}

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingHorizontalXL,
    maxWidth: '960px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  card: {
    padding: tokens.spacingHorizontalL,
  },
  statGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXXL,
    marginTop: tokens.spacingVerticalM,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    color: veracityColors.midnightCharcoal,
  },
  statLabel: {
    color: veracityColors.duskBlue,
    fontSize: tokens.fontSizeBase200,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalM,
  },
  detailsLog: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    backgroundColor: veracityColors.firstLight,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: 'Consolas, monospace',
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    maxHeight: '260px',
    overflowY: 'auto',
  },
  // Numeric table: right-justified values AND headers, faint vertical rules.
  table: {
    marginTop: tokens.spacingVerticalM,
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase300,
  },
  th: {
    textAlign: 'right',
    padding: '4px 12px',
    borderLeft: `1px solid ${veracityColors.firstLight}`,
    color: veracityColors.duskBlue,
    fontWeight: tokens.fontWeightSemibold,
  },
  thLabel: {
    textAlign: 'left',
    padding: '4px 12px 4px 0',
    color: veracityColors.duskBlue,
    fontWeight: tokens.fontWeightSemibold,
  },
  td: {
    textAlign: 'right',
    padding: '4px 12px',
    borderLeft: `1px solid ${veracityColors.firstLight}`,
    fontVariantNumeric: 'tabular-nums',
  },
  tdLabel: {
    textAlign: 'left',
    padding: '4px 12px 4px 0',
  },
});

// =============================================================================
// Page
// =============================================================================

function formatHours(hours: number[], timeZone: string): string {
  const labels = hours.map((h) => {
    const ampm = h < 12 ? 'AM' : 'PM';
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display} ${ampm}`;
  });
  const zone = timeZone === 'America/Chicago' ? 'Central' : timeZone;
  return `${labels.join(', ')} ${zone}`;
}

export function GeocodingPage() {
  const styles = useStyles();

  const statusQuery = useQuery({
    queryKey: ['admin', 'geocodeStatus'],
    queryFn: fetchStatus,
    refetchInterval: 60_000,
  });

  const [runStatus, setRunStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [lastRunWasPreview, setLastRunWasPreview] = useState(false);

  const [diag, setDiag] = useState<Diagnostics | null>(null);
  const [diagStatus, setDiagStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [diagError, setDiagError] = useState<string | null>(null);

  const runGeocode = async (dryRun: boolean) => {
    setRunStatus('running');
    setRunResult(null);
    setLastRunWasPreview(dryRun);
    try {
      const res = await bffFetch('/geocode/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      const data = (await res.json()) as RunResult & { error?: string };
      if (res.ok) {
        setRunResult(data);
        setRunStatus('done');
      } else {
        setRunStatus('error');
        setRunResult({
          processed: 0, geocoded: 0, skipped: 0, errors: 1, capped: false,
          details: [data.error ?? 'Unknown error'],
        });
      }
    } catch (err) {
      setRunStatus('error');
      setRunResult({
        processed: 0, geocoded: 0, skipped: 0, errors: 1, capped: false,
        details: [err instanceof Error ? err.message : String(err)],
      });
    }
  };

  const loadDiagnostics = async () => {
    setDiagStatus('loading');
    setDiagError(null);
    try {
      const res = await bffFetch('/geocode/diagnostics');
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      setDiag((await res.json()) as Diagnostics);
      setDiagStatus('idle');
    } catch (err) {
      setDiagStatus('error');
      setDiagError(err instanceof Error ? err.message : String(err));
    }
  };

  const status = statusQuery.data;
  const nightly = status?.nightly ?? null;

  return (
    <div className={styles.root}>
      <div>
        <Title2>Geocoding</Title2>
        <Caption1 style={{ display: 'block', color: veracityColors.duskBlue, marginTop: '4px' }}>
          Resolves contact and account addresses to coordinates via geocod.io into the
          vba_geocode table, used by the Broker Map and the Groups Maps. Runs nightly;
          the manual run below is for right after a bulk address change. Every run spends
          paid geocod.io credits.
        </Caption1>
      </div>

      {/* ── Nightly status ── */}
      <Card className={styles.card}>
        <Subtitle2>Nightly runs</Subtitle2>
        {statusQuery.isLoading && <Spinner size="tiny" label="Loading status…" />}
        {statusQuery.isError && (
          <MessageBar intent="error">
            <MessageBarBody>Could not load geocode status.</MessageBarBody>
          </MessageBar>
        )}
        {status && (
          <>
            {!status.configured && (
              <MessageBar intent="warning" style={{ marginTop: tokens.spacingVerticalS }}>
                <MessageBarBody>
                  GEOCODIO_API_KEY is not configured on this environment, so nothing geocodes here.
                </MessageBarBody>
              </MessageBar>
            )}
            {nightly?.lastError && (
              <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
                <MessageBarBody>Last nightly run failed: {nightly.lastError}</MessageBarBody>
              </MessageBar>
            )}
            {nightly?.lastSummary && nightly.lastSummary.errors > 0 && !nightly.lastError && (
              <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
                <MessageBarBody>
                  The last nightly pass hit {nightly.lastSummary.errors} errors
                  ({nightly.lastSummary.geocoded} geocoded). If geocoded is 0, check the
                  app identity's vba_geocode privileges (schema:grant-geocode-appuser).
                </MessageBarBody>
              </MessageBar>
            )}
            <Body1 style={{ display: 'block', marginTop: tokens.spacingVerticalS }}>
              {nightly?.enabled
                ? `Enabled: one pass at ${formatHours(nightly.runHours, nightly.timeZone)}.`
                : 'Nightly runs are disabled on this environment.'}
              {nightly?.running ? ' A pass is running right now.' : ''}
            </Body1>
            {nightly?.lastRunAt && (
              <Caption1 style={{ display: 'block', color: veracityColors.duskBlue }}>
                Last pass: {new Date(nightly.lastRunAt).toLocaleString()}
              </Caption1>
            )}
            {nightly?.lastSummary && (
              <div className={styles.statGrid}>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{nightly.lastSummary.geocoded}</span>
                  <span className={styles.statLabel}>geocoded</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{nightly.lastSummary.skipped}</span>
                  <span className={styles.statLabel}>already up to date</span>
                </div>
                <div className={styles.stat}>
                  <span
                    className={styles.statValue}
                    style={nightly.lastSummary.errors > 0 ? { color: veracityColors.morningCoral } : undefined}
                  >
                    {nightly.lastSummary.errors}
                  </span>
                  <span className={styles.statLabel}>errors</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{nightly.lastSummary.capped ? 'yes' : 'no'}</span>
                  <span className={styles.statLabel}>hit per-run cap ({status.maxPerRun})</span>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── Coverage ── */}
      <Card className={styles.card}>
        <Subtitle2>Coverage</Subtitle2>
        <Caption1 style={{ display: 'block', color: veracityColors.duskBlue }}>
          Counts every candidate record and every stored geocode, so it takes a few
          seconds to load.
        </Caption1>
        <div className={styles.controls}>
          <Button
            icon={<ArrowClockwiseRegular />}
            disabled={diagStatus === 'loading'}
            onClick={loadDiagnostics}
          >
            {diagStatus === 'loading' ? 'Loading…' : diag ? 'Reload coverage' : 'Load coverage'}
          </Button>
          {diagStatus === 'loading' && <Spinner size="tiny" />}
        </div>
        {diagStatus === 'error' && (
          <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
            <MessageBarBody>{diagError}</MessageBarBody>
          </MessageBar>
        )}
        {diag && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thLabel}>Records</th>
                <th className={styles.th}>With address</th>
                <th className={styles.th}>Geocoded</th>
                <th className={styles.th}>Missing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.tdLabel}>Accounts</td>
                <td className={styles.td}>{diag.dataverse.accountsWithAddress}</td>
                <td className={styles.td}>{diag.geocodedAccountIds}</td>
                <td className={styles.td}>
                  {Math.max(0, diag.dataverse.accountsWithAddress - diag.geocodedAccountIds)}
                </td>
              </tr>
              <tr>
                <td className={styles.tdLabel}>Contacts</td>
                <td className={styles.td}>{diag.dataverse.contactsWithAddress}</td>
                <td className={styles.td}>{diag.geocodedContactIds}</td>
                <td className={styles.td}>
                  {Math.max(0, diag.dataverse.contactsWithAddress - diag.geocodedContactIds)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {diag && (
          <Caption1 style={{ display: 'block', color: veracityColors.duskBlue, marginTop: tokens.spacingVerticalS }}>
            Geocode table: {diag.geocodeTable.totalRecords} rows
            {Object.entries(diag.geocodeTable.byEntityType)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([type, c]) => `, ${type} ${c.total} (${c.noCoords} without coordinates)`)
              .join('')}
            .
          </Caption1>
        )}
      </Card>

      {/* ── Manual run ── */}
      <Card className={styles.card}>
        <Subtitle2>Manual run</Subtitle2>
        <Caption1 style={{ display: 'block', color: veracityColors.duskBlue }}>
          Preview reports what a run would do without calling geocod.io. Run geocodes up
          to {status?.maxPerRun ?? 1000} records now under your own account.
        </Caption1>
        <div className={styles.controls}>
          <Button
            icon={<EyeRegular />}
            disabled={runStatus === 'running' || !status?.configured}
            onClick={() => runGeocode(true)}
          >
            {runStatus === 'running' ? 'Working…' : 'Preview'}
          </Button>
          <Button
            appearance="primary"
            icon={<PlayRegular />}
            disabled={runStatus === 'running' || !status?.configured}
            onClick={() => runGeocode(false)}
          >
            Run now
          </Button>
          {runStatus === 'running' && <Spinner size="tiny" />}
          {runStatus === 'done' && runResult && (
            <Caption1 style={{ color: veracityColors.duskBlue }}>
              {lastRunWasPreview
                ? `Preview: ${runResult.processed} would be geocoded${runResult.capped ? ' (more beyond the cap)' : ''}.`
                : `${runResult.geocoded} geocoded, ${runResult.skipped} up to date${runResult.errors ? `, ${runResult.errors} errors` : ''}${runResult.capped ? ' (more beyond the cap)' : ''}.`}
            </Caption1>
          )}
          {runStatus === 'error' && runResult && (
            <Caption1 style={{ color: veracityColors.morningCoral }}>
              {runResult.details[0] ?? 'Run failed'}
            </Caption1>
          )}
        </div>
        {runResult && runResult.details.length > 0 && (
          <div className={styles.detailsLog}>{runResult.details.join('\n')}</div>
        )}
      </Card>
    </div>
  );
}

export default GeocodingPage;
